/* global Sys, Core, Services */
Sys.ns("Core");

/**
 * The Auto Play controller.
 *
 * @class Core.AutoPlayerController
 * @extends Core.Controller
 */
Core.AutoPlayerController = {

    /** @inheritdoc */
    constructor: function() {
        Core.AutoPlayerController.superclass.constructor.apply(this, arguments);
    },

    /**
     * @inheritdoc
     */
    setupEvents: function() {
        this.on({
            "request:autoPlayer.stop": this.stop,
            "request:autoPlayer.pause": this.pause,
            "request:autoPlayer.start": this.start,
            "request:autoPlayer.resume": this.resume,

            "notify:responseParser.responseParsed": this.onServerResponse,
            "notify:serverManager.serverErrorReceived": this.stop,

            "notify:stateHandler.leavingIdleState": this.onLeavingIdleState,
            "notify:stateHandler.enteringIdleState": this.onEnteringIdleState,
            "notify:stateHandler.enteringStandardWinPresentationState": this.onEnteringWinPresentationState,
            "notify:stateHandler.enteringBigWinState": this.onEnteringWinPresentationState,
            "notify:spinButton.clicked": this.onSpinButtonClicked,

            "notify.dialogWindow.showingDialog": this.onShowingDialog,
            "notify:dialogWindow.allDialogsClosed": this.onAllDialogsClosed,

            "notify:settingsManager.settingChanged": this.onSettingChanged,

            // Stop autoplay if an error is received
            "notify:errorManager.error": this.stop
        });
    },

    /**
     * Store the server response.
     *
     * @private
     * @param {Object} serverResponse The parsed server response.
     * @returns {void}
     */
    onServerResponse: function(serverResponse) {
        this.model.processServerResponse(serverResponse);
    },

    /**
     * Play a round.
     *
     * @private
     * @returns {void}
     */
    playRound: function() {
        if (this.model.readData("nrRounds") > 0) {
            this.requestRoundStart();
        }
    },

    /**
     * Start auto play if we are not already spinning.
     *
     * @private
     * @returns {void}
     */
    start: function() {
        this.play();

        if (!this.model.readData("spinning") && this.model.readData("isBasicState")) {
            this.requestRoundStart();
        }
    },

    /**
     * Stops auto play.
     *
     * @private
     * @returns {void}
     */
    stop: function() {
        if (!this.model.isState("STOPPED")) {
            this.fireEvent("request:spinButton.removeProperty", "visibleDuringRound", this.MODULE_NAME);
            this.fireEvent("request:spinButton.hideCounter");
            this.fireEvent("request:spinButton.removeDisplayType", "autoPlay");
            this.fireEvent("request:spinButton.removeDisplayType", "autoPlayStop");
            this.fireEvent("request:quickStopper.enableInMode", "autoPlay");
            this.fireEvent("request:playModeManager.removeMode", "autoplay");
            this.model.setState("STOPPED");
            this.model.storeData("nrRounds", 0);
            this.fireEvent("request:settingsManager.storeData", "autoPlayNrSpins", 0);
            this.fireEvent("request:spinButton.enable", this.MODULE_NAME);
            this.fireEvent("request:settingsButton.enable", this.MODULE_NAME);
            this.fireEvent("request:pluginMenu.enable", this.MODULE_NAME);
            this.fireEvent("request:betSettings.enable", this.MODULE_NAME);
            this.fireEvent("request:maxBetButton.enable", this.MODULE_NAME);
            this.fireEvent("notify:autoPlayer.stopped");
        }
    },

    /**
     * Pause auto play.
     *
     * @private
     * @returns {void}
     */
    pause: function() {
        if (this.model.isState("PLAYING")) {
            this.model.setState("PAUSED");
        }
    },

    /**
     * Resume auto play after it has been paused.
     *
     * @private
     * @returns {void}
     */
    resume: function() {
        var me = this;

        if (this.model.isState("PAUSED")) {
            this.model.setState("PLAYING");

            if (!me.model.readData("spinning")) {
                me.playRound();
                // this condition is verified when number of remaining autoplay rounds are zero and state is set to STOPPING
                if (me.model.isState("STOPPING")) {
                    me.stop();
                }
            }

            if (this.model.isState("STOPPING")) {
                me.stop();
            }
        }
    },

    /**
     * Start auto play.
     *
     * @private
     * @returns {void}
     */
    play: function() {
        var me = this;

        if (!me.model.isState("PLAYING") && me.model.readData("nrRounds") > 0) {
            me.model.setState("PLAYING");

            me.fireEvent("request:spinButton.removeDisplayType", "autoPlay");
            me.fireEvent("request:spinButton.addDisplayType", "autoPlayStop");
            me.fireEvent("request:quickStopper.disableInMode", "autoPlay");
            me.fireEvent("request:spinButton.disable", me.MODULE_NAME);
            me.fireEvent("request:betSettings.disable", me.MODULE_NAME);
            me.fireEvent("request:maxBetButton.disable", me.MODULE_NAME);
            me.fireEvent("request:settingsButton.disable", me.MODULE_NAME);
            me.fireEvent("request:pluginMenu.disable", me.MODULE_NAME);
            me.fireEvent("notify:autoPlayer.starting");
            me.fireEvent("request:playModeManager.addMode", "autoplay");
        }
    },

    /**
     * Stops autoplay on spin button clicked (will fire on mobile games).
     *
     * @param {Object} [config] Config object.
     * @param {boolean} config.skip Indicates if the method shall be skipped, in this case not calling stop.
     * @returns {boolean} If spin was clicked or not.
     */
    onSpinButtonClicked: function(config) {
        if (Sys.isDefined(config) && config.hasOwnProperty("skip") && config.skip === true) {
            return false;
        }

        if (this.model.isState("PLAYING") || this.model.isState("PAUSED") || this.model.isState("STOPPING")) {
            this.stop();
        }

        return true;
    },

    /**
     * Play a round or stop depending on the state.
     *
     * @private
     * @returns {void}
     */
    onEnteringIdleState: function() {
        var me = this,
            model = me.model;

        model.storeData("spinning", false);

        if (model.isState("PLAYING")) {
            me.playRound();
        }

        if (model.isState("STOPPING")) {
            me.stop();
        }

        if (model.isState("WAITING")) {
            me.fireEvent("request:playModeManager.addMode", "autoplay");
            me.play();
            me.requestRoundStart();
        }
    },

    /**
     * Start if auto play is waiting or update the counter if it is playing.
     *
     * @private
     * @returns {void}
     */
    onLeavingIdleState: function() {
        var me = this;

        me.model.storeData("spinning", true);

        if (me.model.isState("WAITING")) {
            me.fireEvent("request:playModeManager.addMode", "autoplay");
            me.play();
        }

        if (me.model.isState("PLAYING")) {
            this.notifyNumberOfRounds();
        }
    },

    /**
     * Triggered when entering win presentation state.
     *
     * @private
     * @returns {void}
     */
    onEnteringWinPresentationState: function() {
        var me = this, model = me.model;

        if (model.isState("STOPPING") && Services.settingsManager.getSetting("autoPlayNrSpins") > 0) {
            me.fireEvent("request:settingsManager.storeData", "autoPlayNrSpins", 0);
        }
    },

    /**
     * Pause auto play when a dialog is shown.
     *
     * @private
     * @returns {void}
     */
    onShowingDialog: function() {
        if (this.model.readData("nrRounds") > 0) {
            this.pause();
        }
    },

    /**
     * Resume auto play when all dialogs has been closed.
     *
     * @private
     * @returns {void}
     */
    onAllDialogsClosed: function() {
        if (this.model.readData("nrRounds") > 0) {
            this.resume();
        }
    },

    /**
     * Reduce the number of remaining rounds.
     *
     * @private
     * @returns {void}
     */
    reduceNumberOfRounds: function() {
        // Reduce the number of rounds left
        this.model.playRound();
    },

    /**
     * Notify about the number of remaining rounds.
     *
     * @private
     * @returns {void}
     */
    notifyNumberOfRounds: function() {
        // Update the visual counter
        this.fireEvent("request:spinButton.updateCounter", this.model.readData("nrRounds"));
        this.fireEvent("notify:autoPlayer.reduceCounter", this.model.readData("nrRounds"));
    },

    /**
     * Update based on the setting changed.
     *
     * @private
     * @param {string} setting The name of the setting.
     * @param {*} value The new setting value.
     * @returns {void}
     */
    onSettingChanged: function(setting, value) {
        var me = this;

        if (setting === "autoPlayNrSpins" && value !== me.model.readData("nrRounds")) {
            me.model.storeData("nrRounds", value);

            if (value === 0) {
                me.stop();
            }
            else {
                me.fireEvent("notify:autoPlayer.enabled", value);

                me.fireEvent("request:spinButton.showCounter", value);
                me.fireEvent("request:spinButton.addDisplayType", "autoPlay");
                me.fireEvent("request:spinButton.addProperty", "visibleDuringRound", me.MODULE_NAME);
                me.model.setState("WAITING");
                me.model.storeData("startingBalance", Services.moneyManager.getBalanceCents());
            }
        }
        else if (Sys.contains(["stopAutoplayIfBalanceIncreasedBy", "stopAutoplayIfBalanceDecreasedBy", "stopAutoplayOnAnyWin", "stopAutoplayIfWinExceeds"], setting)) {
            me.model.storeData(setting, value);
        }
    },

    /**
     * Request that a new round should be started.
     *
     * @private
     * @returns {void}
     */
    requestRoundStart: function() {
        this.reduceNumberOfRounds();

        this.fireEvent("notify:autoPlayer.startRound", this.model.readData("nrRounds"));

        Services.gameRound.start();
    }
};

Core.AutoPlayerController = Sys.extend(Core.Controller, Core.AutoPlayerController, "Core.AutoPlayerController");
